// T1 — écran « Prise de commande » : onglets Saisie / File + surcouches Loupe et
// Rendu monnaie. Après validation : ÉCRAN DE CONFIRMATION (LOT 7 §1) —
// « Commande enregistrée · N pizza·s · estimée HH:MM · visible en Production ».
// FIN DE SERVICE : emplacement UNIQUE global (LOT 7 §5) = bas de cet écran,
// discret + confirmation enrichie (récap actives). Aucune logique de transition
// ici (INV-B : réservée à useT1ActionGate).
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useFileCommandes } from '@/hooks/useFileCommandes';
import { useMinuteTick } from '@/hooks/useMinuteTick';
import { FinDeServiceOverlay } from '@/screens/shared/FinDeServiceOverlay';
import { formatHeure } from '@/lib/format';
import { chargeActuelle, versChargeCommandes } from '@/lib/heure-capacite';
import { pluriel } from '@/lib/pluriel';
import { CAMION_CAPACITE_CONFIG } from '@/config/camion.pizznco';
import { SaisieView, type ValidationInfo } from './SaisieView';
import { FileView } from './FileView';
import { Loupe } from './Loupe';
import { RenduMonnaie } from './RenduMonnaie';

type Onglet = 'saisie' | 'file';

export function T1Screen() {
  const [onglet, setOnglet] = useState<Onglet>('saisie');
  const [loupeId, setLoupeId] = useState<number | null>(null);
  const [monnaieId, setMonnaieId] = useState<number | null>(null);
  const [finOuvert, setFinOuvert] = useState(false);
  // Confirmation post-validation (LOT 7 §1) — remplace le contenu tant qu'elle
  // n'est pas fermée par NOUVELLE COMMANDE ou Voir la production.
  const [confirmation, setConfirmation] = useState<ValidationInfo | null>(null);

  const { commandes, lignesParCommande } = useFileCommandes();
  const loupeCommande =
    loupeId === null ? null : commandes.find((c) => c.id === loupeId) ?? null;
  const monnaieLignes =
    monnaieId === null ? null : lignesParCommande.get(monnaieId) ?? [];

  // Bandeau T1 PERMANENT (LOT 7 §3.6) : charge engagée + 1re dispo estimée,
  // rafraîchi à la minute pleine (tick — aucune interaction requise).
  const now = useMinuteTick();
  const charge = chargeActuelle(
    now,
    versChargeCommandes(commandes, lignesParCommande),
    CAMION_CAPACITE_CONFIG,
  );

  return (
    <section aria-labelledby="t1-title" className="flex min-h-[80vh] flex-col">
      <div className="flex flex-col gap-3 p-4">
        <h1 id="t1-title" className="font-jost text-3xl font-bold">
          Prise de commande
        </h1>
        <p
          data-testid="bandeau-charge"
          className="rounded-lg bg-ardoise px-4 py-3 font-jost text-lg text-creme"
        >
          Charge : <span className="font-mono">{charge.pizzas}</span>{' '}
          {pluriel(charge.pizzas, 'pizza')} · ~
          <span className="font-mono">{charge.creneaux}</span>{' '}
          {pluriel(charge.creneaux, 'créneau', 'créneaux')} · 1re dispo estimée{' '}
          <span className="font-mono">{formatHeure(charge.premiereDispo)}</span>
        </p>
        {confirmation === null && (
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant={onglet === 'saisie' ? 'default' : 'outline'}
              onClick={() => setOnglet('saisie')}
              className="min-h-[80px] min-w-[120px] text-xl"
            >
              Saisie
            </Button>
            <Button
              type="button"
              variant={onglet === 'file' ? 'default' : 'outline'}
              onClick={() => setOnglet('file')}
              className="min-h-[80px] min-w-[120px] text-xl"
            >
              File
            </Button>
          </div>
        )}
      </div>

      {confirmation !== null ? (
        <div
          data-testid="confirmation-commande"
          className="flex flex-col gap-6 p-4"
        >
          <p className="font-jost text-3xl">
            Commande enregistrée ·{' '}
            <span className="font-mono">{confirmation.nbPizzas}</span>{' '}
            {pluriel(confirmation.nbPizzas, 'pizza')} · estimée{' '}
            <span className="font-mono">{formatHeure(confirmation.heure)}</span> ·
            visible en Production
          </p>
          <Button
            type="button"
            onClick={() => setConfirmation(null)}
            className="min-h-[120px] text-2xl"
          >
            NOUVELLE COMMANDE
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              window.location.hash = '#/t2';
              setConfirmation(null);
            }}
            className="min-h-[80px] text-xl"
          >
            Voir la production
          </Button>
        </div>
      ) : onglet === 'saisie' ? (
        <SaisieView onValide={setConfirmation} />
      ) : (
        <FileView onLoupe={setLoupeId} onMonnaie={setMonnaieId} />
      )}

      {/* FIN DE SERVICE — emplacement UNIQUE global (LOT 7 §5), discret. */}
      <button
        type="button"
        onClick={() => setFinOuvert(true)}
        className="mt-auto self-center p-6 font-jost text-base text-muted-foreground underline"
      >
        Fin de service
      </button>

      {loupeCommande && (
        <Loupe
          commande={loupeCommande}
          lignes={lignesParCommande.get(loupeCommande.id as number) ?? []}
          onClose={() => setLoupeId(null)}
        />
      )}

      {monnaieId !== null && monnaieLignes && (
        <RenduMonnaie
          lignes={monnaieLignes}
          onClose={() => setMonnaieId(null)}
        />
      )}

      {finOuvert && <FinDeServiceOverlay onClose={() => setFinOuvert(false)} />}
    </section>
  );
}
